import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { FormEvent, useEffect, useState } from "react";
import { request } from "../../common/api";
import useContextMenu from "../../common/hooks/useContextMenu";
import useSelectManager from "../../common/hooks/useSelectManager";
import useEquipmentMonitSummary from "../../common/hooks/useEquipmentMonitSummary";
import { currentShipState } from "../../common/states/ship";
import { GET_EQUIPMENT_OPERATION_STATUS_LED } from "../../common/query-keys";
import ContextMenu from "../../components/molecules/ContextMenu";
import { PageContent } from "../../components/organisms/Contents/PageContent";
import { EquipDataContent } from "../../components/organisms/Contents/EquipDataContent";
import { EquipCurrentContent } from "../../components/organisms/Contents/EquipCurrentContent";
import { EquipAnalysisContent } from "../../components/organisms/Contents/EquipAnalysisContent";
import { EquipmentOperationStatusLED } from "../../common/types/equipment";

import useShipOperationStatus from "../../common/hooks/useShipOperationStatus";
import dayjs from "dayjs";
import { EquipmentDefaultValuesTypes } from "../../types/equipment";
import { EquipmentTemplate } from "../../components/template/EquipmentTemplate";

export const Generator01Page = () => {
  /** ============================= state 영역 ============================= */
  const { index } = useParams();
  const { state } = useLocation();

  const navigate = useNavigate();
  const currentShip = useRecoilValue(currentShipState);

  /** < 계통별알람현황황 > 기간 선택 탭 상태 ( 전1주 / 전월 / 전3개월 ) */
  const [alarmStatusTimeTab, setAlarmStatusTimeTab] = useState<number>(0);

  const defaultValues = {
    shipTypeNo: currentShip?.shipTypeNo,
    shipTypeName: currentShip?.shipTypeName,
    shipNo: currentShip?.shipNo,
    shipName: currentShip?.shipName,
    mcyName: "No.1 발전기",
    mcyCode: "DG1",
    alarmStatusTime: alarmStatusTimeTab,
  };

  // 현재 선택 탭 상태
  const [selectedTab, setSelectedTab] = useState<number>(Number(index) || 0);
  // <조회조건> 검색버튼을 위한 상태 관리
  const [searchDefaultValues, setSearchDefaultValues] =
    useState<EquipmentDefaultValuesTypes>(defaultValues);
  // 개요 탭 > 계통별 알람현황 그래프 클릭시 조회 조건 반영
  const [chartSearch, setChartSearch] =
    useState<EquipmentDefaultValuesTypes>(defaultValues);
  /** 조회조건 핸들러 hooks */
  const { search, setSearchData } = useSelectManager(searchDefaultValues);

  /** 개요탭 조회 데이터 hooks
   * * [ 개요 탭 ] 조회 데이터 조회 완료 2025.05.23
   */
  const {
    /** 장비운용현황 시간 */
    equipmentOperationStatusTime,
    /** 계통별 알람현황황 */
    alarmStatusData,
    /** 센서 알람현황 */
    sensorAlarmData,
    /** 전체 장비 운용의 장비 버튼 색( 해당 장비에 속한 센서의 worst case) */
    equipmentOperationStatusLED,
  } = useEquipmentMonitSummary(searchDefaultValues);

  // 상단 함정 운항 정보 조회
  // * 함정 운항 정보 조회 완료 2025.05.23
  const { shipOperationStatus } = useShipOperationStatus({
    shipTypeNo: currentShip.shipTypeNo,
    shipNo: currentShip.shipNo,
  });

  /** 상태진단 검색 버튼 클릭시 상태변화 감지 state */
  const [searchAnalysisConfirm, setSearchAnalysisConfirm] = useState<any>();

  /**
   * * [ 상태진단 탭 ] 조회 조건 관리 hooks 완료 2025.05.23
   */
  const {
    search: analysisSearch,
    /** 계통 select options */
    lowMachinerySelectOptions: analysisLowMachinerySelectOptions,
    /** 센서 select options */
    sensorSelectOptions: analysisSensorSelectOptions,
    /** 계통 선택 변경 */
    handleLowMcySelectChange: analysisHandleLowMcySelectChange,
    /** 센서 값 변경 */
    handleSensorChange: analysisHandleSensorChange,
    setSearchData: analysisSetSearchData,
  } = useSelectManager(searchDefaultValues);

  /** ============================= API 영역 ============================= */

  /** ============================= 비즈니스 로직 영역 ============================= */

  /**
   * 개요 탭 > 계통별 알람현황 그래프 클릭시 이벤트 핸들러
   */
  const onLowMachinaryGraphClicked = (params: any) => {
    setChartSearch({
      shipTypeNo: currentShip.shipTypeNo,
      shipNo: currentShip.shipNo,
      mcyCode: "DG1",
      mcyName: "No.1 발전기",
      lowMcyName: params.name,
      startDate: dayjs(alarmStatusData?.startDate).format(
        "YYYY-MM-DD 00:00:00"
      ),
      endDate: dayjs(alarmStatusData?.endDate).format("YYYY-MM-DD 23:59:59"),
    });
    navigate(`/equipment/generator01/2`);
  };

  const setSelectedTabHandler = (index: number) => {
    navigate(`/equipment/generator01/${index}`);
  };

  const {
    contextMenu,
    handleContextMenu,
    handleOpenInNewTab,
    handleOpenInNewWindow,
  } = useContextMenu();

  /** <상태진단> 조회조건 검색버튼 핸들러 ( 돋보기 ) */
  const equipAnalysisHandleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // useQuery, queryKey 조건 상태 트리거
    setSearchAnalysisConfirm({
      shipTypeNo: search.shipTypeNo,
      shipNo: search.shipNo,
      mcyName: search.mcyName,
      lowMcyName: analysisSearch.lowMcyName,
      sensorCode: analysisSearch.sensorCode,
      startDate: analysisSearch.startDate,
      endDate: analysisSearch.endDate,
    });
  };

  /** ============================= 컴포넌트 영역 ============================= */

  const tabs = [
    {
      index: 0,
      name: "개요",
      component: (
        <EquipCurrentContent
          // 장비 운용현황 => 장비 작동 시간
          equipmentOperationStatusTime={equipmentOperationStatusTime}
          // 장비 운용현황 => 예측 모델 worst case LED 상태
          equipmentOperationStatusLED={equipmentOperationStatusLED?.generator1}
          // 계통별 알람현황
          alarmStatusData={alarmStatusData}
          // 센서 알람현황
          sensorAlarmData={sensorAlarmData}
          // <계통별 알람현황 > 기간 선택 탭 상태 ( 전1주 / 전월 / 전3개월 )
          alarmStatusTimeTab={alarmStatusTimeTab}
          setAlarmStatusTimeTab={setAlarmStatusTimeTab}
          // 센서 알람현황 차트 column의 차트 아이콘 클릭시 이벤트 핸들러
          onLowMachinaryGraphClicked={onLowMachinaryGraphClicked}
        />
      ),
    },
    {
      index: 1,
      name: "상태진단",
      component: (
        <EquipAnalysisContent
          search={analysisSearch}
          searchConfirm={searchAnalysisConfirm}
          lowMachinerySelectOptions={analysisLowMachinerySelectOptions}
          sensorSelectOptions={analysisSensorSelectOptions}
          handleLowMcySelectChange={analysisHandleLowMcySelectChange}
          handleSensorChange={analysisHandleSensorChange}
          setSearchData={analysisSetSearchData}
          handleSearch={equipAnalysisHandleSearch}
        />
      ),
    },
    {
      index: 2,
      name: "상태진단 알람조회",
      component: (
        <EquipDataContent
          search={chartSearch}
          localCurrentShip={currentShip}
          setSearch={setChartSearch}
        />
      ),
    },
  ];

  /** ============================= useEffect 영역 ============================= */

  useEffect(() => {
    setSelectedTab(Number(index));
  }, [index]);

  useEffect(() => {
    setSearchDefaultValues((prevValues) => ({
      ...prevValues,
      alarmStatusTime: alarmStatusTimeTab,
    }));
  }, [alarmStatusTimeTab]);

  // 다른 페이지에서 마우스 오른쪽 클릭으로 상태진단 탭으로 이동 페이지 이동 시 값 세팅
  useEffect(() => {
    // state 값이 null일때는 기본적인 페이지이동이기 때문에 세팅하지 않음
    if (state !== null) {
      const selectedDate = new Date(state?.search?.startDate);
      const nextWeek = new Date(
        selectedDate?.getTime() + 7 * 24 * 60 * 60 * 1000
      );
      const startDate = selectedDate.toISOString().split("T")[0];
      const endDate = nextWeek.toISOString().split("T")[0];

      setSearchAnalysisConfirm({
        shipTypeNo: state?.search?.shipTypeNo,
        shipNo: state?.search?.shipNo,
        mcyName: state?.search?.mcyName,
        lowMcyName: state?.search?.lowMcyName,
        sensorCode: state?.search?.sensorCode,
        startDate: startDate,
        endDate: endDate,
      });

      setSearchData("shipTypeNo", state?.search?.shipTypeNo);
      setSearchData("shipNo", state?.search?.shipNo);
      setSearchData("mcyName", state?.search?.mcyName);
      analysisSetSearchData("lowMcyName", state?.search?.lowMcyName);
      analysisSetSearchData("sensorCode", state?.search?.sensorCode);
      analysisSetSearchData("startDate", startDate);
      analysisSetSearchData("endDate", endDate);

      setSearchData("startDate", startDate);
      setSearchData("endDate", endDate);
    }
  }, [state]);

  return (
    <PageContent depth01Title="장비 모니터링" depth02Title="No.1 발전기">
      <>
        <EquipmentTemplate
          localCurrentShip={currentShip}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTabHandler}
          onContextMenu={handleContextMenu}
          data={tabs}
          shipOperationStatus={shipOperationStatus}
        />

        <ContextMenu
          handleOpenInNewTab={handleOpenInNewTab}
          handleOpenInNewWindow={handleOpenInNewWindow}
          visible={contextMenu.visible}
          x={contextMenu.x}
          y={contextMenu.y}
        />
      </>
    </PageContent>
  );
};
